import {IErrorResult} from "../Basic/api";
import {z} from "zod";
import moment from "moment";

export interface IErrorResultWithRetryData extends IErrorResult {
    retryData: number
}

export interface IServerErrorWithRetryData {
    response: {
        data: {
            error: IErrorResultWithRetryData
        },
        headers: {
            'retry-after': number
        }
    }
}

export const RecoverySearchUser = z.object({
    id: z.string(),
    name: z.string(),
    surname: z.string(),
    patronymic: z.string(),
    avatar: z.object({
        id: z.number(),
        path: z.string()
    }).nullable(),
    sex_id: z.number().nullable(),
    email: z.string().email()
})

export type ISearchUser = z.infer<typeof RecoverySearchUser>

export interface ISearchUserResponse {
    data: ISearchUser
    error?: IErrorResult
}

export const RecoverySendCodeData = z.object({
    retry_after: z.number(),
    token: z.string(),
    expires_in: z.string().refine(string => moment(string, 'YYYY-MM-DD HH:mm:ss').isValid()),
    recovery_type: z.number(),
    sent_to: z.string()
})

export type IRecoverySendCodeData = z.infer<typeof RecoverySendCodeData>

export interface IRecoverySendCodeResponse {
    data: IRecoverySendCodeData,
    error: IErrorResult
}

export interface IRepeatRecoveryCodeResponse {
    data: number,
    error: IErrorResult
}

export interface IRecoverySendCodeEmail {
    data: string,
    type: number
}

export interface IConfirmRecoveryCodeRequest {
    token: string
    code: string
}

export interface IUpdatePasswordRequest {
    token: string
    password: string
    password_repeat: string
    ad_person_id: number | null
    phone: string | null
}

export interface IGetRecoveryPhoneResponse {
    data: string
    error: IErrorResult
}

export interface IRegisterFormProps {
    user: {
        phone: string
        email: string
    }
    person: {
        first_name: string
        last_name: string
        patronymic: string | null
        birthdate: string | undefined
    }
    identityCard: {
        identity_card_type_id: number | null
        series: string | null
        number: string
    }
}

export interface IRegisterProps {
    phone: string
    email: string
    citizenshipId: number | null
    name: string
    surname: string
    patronymic: string | null
    birthDate: string
    passportSeries: string | null
    passportNumber: string
    password?: string
    passwordRepeat?: string
}

export interface IRegisterFormData {
    autorization_status: number
    user: {
        id: string
        email: {
            email: string
            is_verified: boolean
        }
        phone: {
            phone: string
            is_verified: boolean
        }
        is_passport: boolean
    }
    person: {
        id: string
        name: string
        surname: string
        patronymic: string | null
        birthdate: string
    }
    identity_card: {
        citizenship_id: string
        series: string | null
        number: string
    }
}

export interface IRegisterFormResponse {
    data: IRegisterFormData
    error: IErrorResult
}

export const ConnectionModel = z.object({
    common_name: z.string(),
    email: z.string(),
    ad_id: z.number(),
})

export type IConnection = z.infer<typeof ConnectionModel>

export const AccountModel = z.object({
    email: z.string(),
    fio: z.string(),
    ad_person_id: z.number(),
})

export type IAccount = z.infer<typeof AccountModel>

const CheckConnectionsModel = z.object({
    access_token: z.string(),
    expires_in: z.string().refine(string => moment(string, 'YYYY-MM-DD HH:mm:ss').isValid()),
    connections: z.array(ConnectionModel),
    need_auth: z.boolean(),
    email: z.string().nullable(),
    accounts: z.array(AccountModel),
    password_expiry_days_left: z.number().nullable().optional(),
    password_expiry_at: z.string().nullable().optional(),
    need_second_factor: z.boolean().optional(),
    second_factor_operation_id: z.number().nullable().optional(),
    second_factor_total_ttl_minutes: z.number().nullable().optional(),
    second_factor_way_id: z.number().nullable().optional(),
    second_factor_service_phone: z.string().nullable().optional(),
    person: z.object({
        name: z.string().nullable().optional(),
        surname: z.string().nullable().optional(),
    }).optional()
})

export type ICheckConnection = z.infer<typeof CheckConnectionsModel>
export interface ICheckConnectionsResponse {
    data: ICheckConnection
    error: IErrorResult
}

const ConfirmRecoveryCodeModel = z.object({
    accounts: z.array(AccountModel)
})
export type IConfirmRecoveryCode = z.infer<typeof ConfirmRecoveryCodeModel>
export interface IConfirmRecoveryCodeResponse {
    data: IConfirmRecoveryCode
    error: IErrorResult
}


// Вход админа под пользователем
const CheckConnectionsAdminModel = z.object({
    access_token: z.string(),
    expires_in: z.string().refine(string => moment(string, 'YYYY-MM-DD HH:mm:ss').isValid()),
    need_second_factor: z.boolean().optional(),
    second_factor_operation_id: z.number().nullable().optional(),
    second_factor_total_ttl_minutes: z.number().nullable().optional(),
    second_factor_way_id: z.number().nullable().optional(),
    second_factor_service_phone: z.string().nullable().optional(),
})

export type ICheckConnectionAdmin = z.infer<typeof CheckConnectionsAdminModel>

export interface ICheckConnectionsAdminResponse {
    data: ICheckConnectionAdmin
    error: IErrorResult
}

export interface IConnectionResponse {
    data: null
    error: IErrorResult
}

export const RegistrationInitDataModel = z.object({
    phone: z.string().nullable(),
    email: z.string(),
    surname: z.string().nullable(),
    name: z.string().nullable(),
    patronymic: z.string().nullable().optional(),
    birthdate: z.string().refine(value => moment(value, 'YYYY-MM-DD').isValid()).nullable(),
    passportType: z.object({
        id: z.number().nullable(),
        name: z.string().nullable(),
    }),
    passportSeries: z.string().nullable(),
    passportNumber: z.string().nullable(),
}).nullable()

export type IRegistrationInitData = z.infer<typeof RegistrationInitDataModel>

export interface IRegistrationInitResult {
    data: IRegistrationInitData
    error: IErrorResult
}

export const ConfirmationRequirementsModel = z.object({
    phone_verification: z.boolean()
})

export type IConfirmationRequirements = z.infer<typeof ConfirmationRequirementsModel>

export interface IConfirmationRequirementsResult {
    data: IConfirmationRequirements
    error: IErrorResult
}

export interface IGetLKLinkResponse {
    data: {
        link: string
    }
    error: IErrorResult
}

export interface IPerson {
    id: string
    fio: string
    email: string
}

export interface IPersons {
    data: IPerson[]
}

